/**
 * A-01 «Старт».
 *
 * ПС §06 экран 1: логотип, короткое обещание продукта, вход и ссылки на
 * документы. Композиция — как у стартовых экранов мессенджеров и приложений
 * знакомств (Telegram, Bumble, Hinge — референсы Mobbin): фирменный фон на
 * весь экран, знак и слоган по центру, действия внизу. Всё помещается в одну
 * высоту экрана на любом телефоне — прокрутки нет, главная кнопка всегда видна.
 *
 * Разделение «Создать аккаунт / Войти» убрано намеренно: ответ сервера
 * одинаков для нового и существующего аккаунта, и выбор ничего не менял.
 *
 * На телефоне рядом с «Начать» стоит «Установить приложение»: инструкция
 * открывается листом (шаги для iPhone, магазины и веб-версия для Android).
 * Установка предлагается до входа, потому что установленный веб-апп на iPhone —
 * отдельный контекст: сессия вкладки в него не переносится.
 */
import { Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { MEDIA, useMediaQuery } from '@shared/lib/use-media-query'
import { installVariant, promptInstall, subscribeInstallState } from '@shared/pwa/install'
import { isIosLike, isStandalone } from '@shared/pwa/platform'
import { Button, Icon, LogoTile, SegmentedControl, Sheet, cx, useToast, type IconName } from '@shared/ui'

import { ScreenFrame } from '../../layouts/ScreenFrame'
import { getNavigationDirection } from '../../model/navigation-direction'
import { INSTALL_STEPS } from '../system/InstallApp'
import styles from './start.module.css'

type Platform = 'android' | 'ios'

const POINTS: readonly { icon: IconName; text: string }[] = [
  { icon: 'dvizh', text: 'Видно, почему вы сойдётесь' },
  { icon: 'chats', text: 'Чаты с контактами и после взаимного «Го»' },
  { icon: 'shield', text: 'Блокировка и жалоба в один шаг' },
]

export function StartScreen(): React.ReactElement {
  const navigate = useNavigate()
  const isMobile = useMediaQuery(MEDIA.mobile)
  const standalone = isStandalone()

  /*
   * Знак, слоган и кнопки появляются с анимацией только когда экран показан
   * впервые. При возврате «назад» она играла заново поверх движения самого
   * экрана — логотип дёргался на каждом возврате со страницы ввода номера.
   */
  const [intro] = useState(() => getNavigationDirection() !== 'back')

  /*
   * Инструкция нужна там, где приложение ставится: телефон и планшет iPad
   * (он отдаёт «настольную» ширину). В установленном приложении её нет.
   */
  const showGuide = !standalone && (isMobile || isIosLike())
  const [guideOpen, setGuideOpen] = useState(false)
  // Вкладка по умолчанию — платформа, с которой пришёл человек.
  const [platform, setPlatform] = useState<Platform>(() => (isIosLike() ? 'ios' : 'android'))

  return (
    <>
      <ScreenFrame className={styles['page']}>
        <div className={styles['backdrop']} aria-hidden>
          <div className={styles['pattern']} />
          <div className={styles['glow']} />
        </div>

        <main className={cx(styles['main'], intro && styles['intro'])}>
          <section className={styles['hero']}>
            <span className={styles['logo']}>
              <LogoTile size={104} label="Движ" />
            </span>
            <h1 className={styles['name']}>Движ</h1>
            <p className={styles['tagline']}>Друзья по интересам — рядом с вами</p>
            <p className={styles['lead']}>
              Люди, с которыми есть что делать вместе: спорт, настолки, кофе, походы — «Движ» подберёт и
              объяснит почему.
            </p>

            <ul className={styles['points']}>
              {POINTS.map((point) => (
                <li key={point.icon} className={styles['point']}>
                  <Icon name={point.icon} size={16} />
                  {point.text}
                </li>
              ))}
            </ul>
          </section>

          <div className={styles['actions']}>
            <button type="button" className={styles['cta']} onClick={() => void navigate({ to: '/auth' })}>
              Начать
            </button>

            {showGuide ? (
              <button type="button" className={styles['ctaGhost']} onClick={() => setGuideOpen(true)}>
                <Icon name="download" size={18} />
                Установить приложение
              </button>
            ) : null}

            {!showGuide && !standalone ? (
              <p className={styles['hint']}>Приложение для телефона — откройте dvizh.chat со смартфона.</p>
            ) : null}

            <nav className={styles['legal']} aria-label="Документы">
              <Link to="/legal/$document" params={{ document: 'terms' }} className={styles['legalLink']}>
                Условия
              </Link>
              <Link to="/legal/$document" params={{ document: 'privacy' }} className={styles['legalLink']}>
                Приватность
              </Link>
              <Link
                to="/legal/$document"
                params={{ document: 'community_rules' }}
                className={styles['legalLink']}
              >
                Правила
              </Link>
            </nav>
          </div>
        </main>
      </ScreenFrame>

      {/* Установка — листом: экран остаётся коротким, а шаги читаются крупно */}
      <Sheet
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        title="Установить приложение"
        description="Отдельная иконка, запуск без адресной строки и уведомления."
      >
        <div className={styles['guide']}>
          <SegmentedControl
            fullWidth
            value={platform}
            onValueChange={setPlatform}
            aria-label="Платформа"
            segments={[
              { value: 'android', label: 'Android' },
              { value: 'ios', label: 'iPhone' },
            ]}
          />

          {/* key — чтобы смена вкладки проигрывала анимацию появления заново */}
          <div key={platform} className={styles['panel']}>
            {platform === 'android' ? <AndroidGuide onInstalled={() => setGuideOpen(false)} /> : <IosGuide />}
          </div>

          <Button variant="ghost" fullWidth onClick={() => setGuideOpen(false)}>
            Позже — начать в браузере
          </Button>
        </div>
      </Sheet>
    </>
  )
}

/* ===================================================================== */
/* Android                                                                */
/* ===================================================================== */

/**
 * Магазин и APK — заготовки: сборок ещё нет, поэтому строки помечены «скоро»
 * и честно об этом говорят вместо ссылки в никуда. Рабочий путь сегодня —
 * установка веб-версии, её и предлагаем следующим шагом.
 */
function AndroidGuide({ onInstalled }: { onInstalled: () => void }): React.ReactElement {
  const toast = useToast()
  const [variant, setVariant] = useState(() => installVariant())
  const [manual, setManual] = useState(false)

  // `beforeinstallprompt` приходит и после первой отрисовки.
  useEffect(() => subscribeInstallState(() => setVariant(installVariant())), [])

  const install = async () => {
    const outcome = await promptInstall()
    // Диалога нет — остаётся путь через меню браузера, его и показываем.
    if (outcome === 'unavailable') setManual(true)
    if (outcome === 'accepted') {
      toast.show('Готово — «Движ» появится на экране')
      onInstalled()
    }
  }

  return (
    <>
      <ul className={styles['stores']}>
        <li>
          <button
            type="button"
            className={styles['store']}
            onClick={() => toast.show('RuStore: публикация готовится')}
          >
            <span className={styles['storeIcon']}>
              <Icon name="download" size={20} />
            </span>
            <span className={styles['storeBody']}>
              <span className={styles['storeName']}>
                RuStore
                <span className={styles['soon']}>скоро</span>
              </span>
              <span className={styles['storeHint']}>Магазин приложений для Android</span>
            </span>
            <Icon name="chevron-right" size={18} />
          </button>
        </li>
        <li>
          <button
            type="button"
            className={styles['store']}
            onClick={() => toast.show('APK появится вместе с первым релизом')}
          >
            <span className={styles['storeIcon']}>
              <Icon name="download" size={20} />
            </span>
            <span className={styles['storeBody']}>
              <span className={styles['storeName']}>
                Файл APK
                <span className={styles['soon']}>скоро</span>
              </span>
              <span className={styles['storeHint']}>Установка напрямую, без магазина</span>
            </span>
            <Icon name="chevron-right" size={18} />
          </button>
        </li>
      </ul>

      <div className={styles['divider']}>
        <span>а пока</span>
      </div>

      <Button variant="primary" size="lg" fullWidth onClick={() => void install()}>
        Установить веб-версию
      </Button>

      {manual || variant !== 'chromium' ? (
        <p className={styles['note']}>
          Если диалог не открылся, приложение ставится из меню браузера: пункт «Установить приложение».
        </p>
      ) : null}
    </>
  )
}

/* ===================================================================== */
/* iPhone                                                                 */
/* ===================================================================== */

/** Те же шаги, что и в PWA-02, плюс главное про уведомления на iOS. */
function IosGuide(): React.ReactElement {
  return (
    <>
      <ol className={styles['steps']}>
        {INSTALL_STEPS.map((step, index) => (
          <li key={step.title}>
            <span className={styles['stepNumber']}>{index + 1}</span>
            <span className={styles['stepBody']}>
              <span className={styles['stepTitle']}>
                <Icon name={step.icon} size={16} />
                {step.title}
              </span>
              <span className={styles['stepHint']}>{step.hint}</span>
            </span>
          </li>
        ))}
      </ol>

      <div className={cx(styles['callout'])}>
        <Icon name="bell" size={18} />
        <span>
          Откройте «Движ» с экрана «Домой» и разрешите уведомления: во вкладке браузера на iPhone они не
          работают.
        </span>
      </div>
    </>
  )
}
